import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  Package,
  DollarSign,
  BarChart3,
  Users,
  ShoppingCart
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, RawMaterial, InventoryTransaction } from '../../services/dataService';

const InventoryAnalytics: React.FC = () => {
  const { tenantData } = useAuth();
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [materialsData, transactionsData] = await Promise.all([
        dataService.getRawMaterials(tenantData!.id),
        dataService.getInventoryTransactions(tenantData!.id)
      ]);
      setRawMaterials(materialsData);
      setTransactions(transactionsData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadData();
    }
  }, [tenantData, loadData]);

  // Calculate analytics
  const totalItems = rawMaterials.length;
  const lowStockItems = rawMaterials.filter(item => item.currentStock <= item.minStock);
  const overStockItems = rawMaterials.filter(item => item.currentStock >= item.maxStock);
  const totalInventoryValue = rawMaterials.reduce((sum, item) => sum + (item.currentStock * item.costPerUnit), 0);

  // Recent transactions (last 30 days)
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const recentTransactions = transactions.filter(t => new Date(t.createdAt!) >= thirtyDaysAgo);
  const stockInTransactions = recentTransactions.filter(t => t.type === 'in');
  const stockOutTransactions = recentTransactions.filter(t => t.type === 'out');
  const totalStockInCost = stockInTransactions.reduce((sum, t) => sum + (t.totalCost || 0), 0);

  // Category breakdown
  const categoryStats = rawMaterials.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = {
        count: 0,
        value: 0,
        lowStock: 0
      };
    }
    acc[item.category].count++;
    acc[item.category].value += item.currentStock * item.costPerUnit;
    if (item.currentStock <= item.minStock) {
      acc[item.category].lowStock++;
    }
    return acc;
  }, {} as Record<string, { count: number; value: number; lowStock: number }>);

  // Top suppliers by value
  const supplierStats = rawMaterials.reduce((acc, item) => {
    const supplier = item.supplier || 'Unknown';
    if (!acc[supplier]) {
      acc[supplier] = {
        items: 0,
        value: 0
      };
    }
    acc[supplier].items++;
    acc[supplier].value += item.currentStock * item.costPerUnit;
    return acc;
  }, {} as Record<string, { items: number; value: number }>);

  const topSuppliers = Object.entries(supplierStats)
    .sort(([,a], [,b]) => b.value - a.value)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-page-enter">
      {/* Header */}
      <div className="text-center sm:text-left">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Inventory Analytics
        </h2>
        <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg">
          Comprehensive insights and reports for your inventory management
        </p>
      </div>

      {/* Key Metrics - Primary Stats Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Items Card */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-100 group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-r from-blue-400 to-blue-600 p-2 sm:p-3 rounded-xl group-hover:rotate-12 transition-transform duration-300">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 dark:text-blue-400 group-hover:rotate-12 transition-transform duration-300" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium mb-1">
              Total Items
            </p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {totalItems}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              In inventory
            </p>
          </div>
        </div>

        {/* Low Stock Items Card */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-200 group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-r from-orange-400 to-orange-600 p-2 sm:p-3 rounded-xl group-hover:rotate-12 transition-transform duration-300">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 dark:text-orange-400 group-hover:rotate-12 transition-transform duration-300" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium mb-1">
              Low Stock Items
            </p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600 dark:text-orange-400">
              {lowStockItems.length}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Need restocking
            </p>
          </div>
        </div>

        {/* Inventory Value Card */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-300 group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-r from-green-400 to-green-600 p-2 sm:p-3 rounded-xl group-hover:rotate-12 transition-transform duration-300">
              <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 dark:text-green-400 group-hover:rotate-12 transition-transform duration-300" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium mb-1">
              Inventory Value
            </p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400">
              ₹{totalInventoryValue.toFixed(0)}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total worth
            </p>
          </div>
        </div>

        {/* Monthly Purchases Card */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-2xl transition-all duration-700 ease-out hover:-translate-y-2 sm:hover:-translate-y-4 hover:scale-105 animate-fade-in-up animation-delay-400 group">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-gradient-to-r from-purple-400 to-purple-600 p-2 sm:p-3 rounded-xl group-hover:rotate-12 transition-transform duration-300">
              <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 dark:text-purple-400 group-hover:rotate-12 transition-transform duration-300" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-medium mb-1">
              Monthly Purchases
            </p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600 dark:text-purple-400">
              ₹{totalStockInCost.toFixed(0)}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Last 30 days
            </p>
          </div>
        </div>
      </div>

      {/* Secondary Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Stock Status Overview */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-500">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              Stock Status Overview
            </h3>
            <div className="bg-gradient-to-r from-indigo-400 to-indigo-600 p-2 rounded-xl">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Good Stock</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                {rawMaterials.filter(item => 
                  item.currentStock > item.minStock * 1.5 && item.currentStock < item.maxStock
                ).length}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Medium Stock</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                {rawMaterials.filter(item => 
                  item.currentStock > item.minStock && item.currentStock <= item.minStock * 1.5
                ).length}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Low Stock</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">{lowStockItems.length}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Over Stock</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">{overStockItems.length}</span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-600">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              Recent Activity (30 Days)
            </h3>
            <div className="bg-gradient-to-r from-teal-400 to-teal-600 p-2 rounded-xl">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Stock In</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">{stockInTransactions.length}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Stock Out</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">{stockOutTransactions.length}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
                <span className="text-sm sm:text-base text-gray-700 dark:text-gray-200">Total Purchases</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">₹{totalStockInCost.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            Category Breakdown
          </h3>
          <div className="bg-gradient-to-r from-blue-400 to-blue-600 p-2 rounded-xl">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Category</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Items</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Total Value</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Low Stock</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categoryStats).map(([category, stats], index) => (
                <tr key={category} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200" style={{animationDelay: `${0.8 + index * 0.1}s`}}>
                  <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{category}</td>
                  <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{stats.count}</td>
                  <td className="py-3 px-4 text-gray-700 dark:text-gray-300">₹{stats.value.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    {stats.lowStock > 0 ? (
                      <span className="text-red-600 dark:text-red-400 font-medium">{stats.lowStock}</span>
                    ) : (
                      <span className="text-green-600 dark:text-green-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Suppliers */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            Top Suppliers by Inventory Value
          </h3>
          <div className="bg-gradient-to-r from-purple-400 to-purple-600 p-2 rounded-xl">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
        </div>
        <div className="space-y-3">
          {topSuppliers.map(([supplier, stats], index) => (
            <div key={supplier} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors duration-200 group" style={{animationDelay: `${0.9 + index * 0.1}s`}}>
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-primary-400 to-primary-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm group-hover:scale-110 transition-transform duration-200">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{supplier}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{stats.items} items</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900 dark:text-white">₹{stats.value.toFixed(2)}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {((stats.value / totalInventoryValue) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border border-red-200 dark:border-red-800 rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up animation-delay-900">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-gradient-to-r from-red-400 to-red-600 p-2 rounded-xl">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-red-800 dark:text-red-200">
              Items Requiring Attention
            </h3>
          </div>
          <div className="space-y-2">
            {lowStockItems.slice(0, 5).map((item, index) => (
              <div key={item.id} className="flex items-center justify-between p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors duration-200" style={{animationDelay: `${1.0 + index * 0.1}s`}}>
                <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
                <span className="text-red-600 dark:text-red-400 font-medium">
                  {item.currentStock} / {item.minStock} {item.baseUnit}
                </span>
              </div>
            ))}
            {lowStockItems.length > 5 && (
              <p className="text-red-700 dark:text-red-300 text-sm mt-2">
                +{lowStockItems.length - 5} more items need restocking
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryAnalytics; 